/* 
  Add all types here that are used in the project
  no need to create new files for each type specifically
*/

export enum Localization {
  English = "en",
  Finnish = "fin",
}

export interface IGalleryItem {
  titleEn: string;
  titleFi: string;
  dateEn: string;
  dateFi: string;
  place: string;
  link: string;
  image: string[];
}

export enum RoleKey {
  CASTER = "CASTER",
  HOST = "HOST",
}

export interface IStaff {
  id: string;
  name: string;
  img: string;
  link: string | null;
  handle: string;
  role: RoleKey;
  description_en: string | null;
  description_fin: string | null;
}

export interface ITeam {
  team: string;
  id: number;
  src: string;
  wins: number;
  losses: number;
  position: number;
}

export interface ITeamScores {
  id: string;
  name: string;
  wins: number;
  losses: number;
  gameWins: number;
  gameLosses: number;
}

export interface ITeams {
  name: string;
  id: string;
  type: string;
  desciption_en: string;
  description_fin: string;
  website: string;
  twitter: string;
  discord: string;
  players: IPlayer[];
  image: string;
  wins: string;
  losses: string;
}
export interface IMatch {
  id: number;
  time: string;
  teamOne: string;
  teamOneName: string;
  teamTwo: string;
  teamTwoName: string;
  teamOneScore: number;
  teamTwoScore: number;
  matchType: string;
  link: string;
  youtubeLink?: string;
  season?: string;
  status?: string;
  bracket?: string;
  bracketRound?: number;
  bracketPosition?: number;
}

export interface IBanList {
  championId: number;
  pickTurn: number;
}

export interface IMatchGamePlayer {
  team_id: number;
  puuid: string;
  champion_id: number;
  champion_name: string;
  champions_killed: number;
  deaths: number;
  assists: number;
  role: string;
  gold_earned: number;
  level: number;
  damage: number;
  vision_score: number;
  wards_placed: number;
  wards_killed: number;
  creep_score: number;
  items: number[];
  cs_at_15: number;
  double_kills: number;
  triple_kills: number;
  quadra_kills: number;
  penta_kills: number;
  summoner1_id: number;
  summoner2_id: number;
}

export interface IGameRecord {
  game_id: number;
  match_id: number;
  duration: number;
  blue_grubs: number;
  blue_herald: boolean;
  blue_turrets: number;
  blue_dragons: number;
  blue_barons: number;
  blue_win: boolean;
  /** Go nil slices may arrive as JSON null. */
  blue_bans: IBanList[] | null;
  blue_dragon_types: string[] | null;
  blue_elders: number;
  red_grubs: number;
  red_herald: boolean;
  red_turrets: number;
  red_dragons: number;
  red_barons: number;
  red_win: boolean;
  red_bans: IBanList[] | null;
  red_dragon_types: string[] | null;
  red_elders: number;
  players: IMatchGamePlayer[] | null;
}

export interface IMatchDetail {
  match: IMatch;
  /** Go nil slices encode as JSON null; always coalesce before use. */
  games: IGameRecord[] | null;
  playerStats: IPlayerGameStats[] | null;
}

export interface IPlayer {
  id: string;
  riot_puuid: string;
  name: string;
  displayName: string;
  tagline: string;
  link: string;
  teamId: string;
  position: PlayerPosition;
  playerType: string;
  leaguePoint: number;
  rank: string;
  wins: number;
  losses: number;
  championMastery: ChampionMastery[];
}
export type UpcomingMatch = Omit<
  IMatch,
  "teamOne" | "teamTwo" | "teamOneName" | "teamTwoName" | "teamOneScore" | "teamTwoScore"
> &
  Partial<
    Pick<
      IMatch,
      "teamOne" | "teamTwo" | "teamOneName" | "teamTwoName" | "teamOneScore" | "teamTwoScore"
    >
  >;

export interface IRecentGame {
  date: string;
  teams: Array<{
    name: string;
    src: string;
    week: string;
  }>;
  result: {
    team1_score: number;
    team2_score: number;
  };
}

export enum WeekDays {
  MON = 1,
  TUE = 2,
  WED = 3,
  THU = 4,
  FRI = 5,
  SAT = 6,
  SUN = 0,
}

export enum Months {
  JAN = 0,
  FEB = 1,
  MAR = 2,
  APR = 3,
  MAY = 4,
  JUN = 5,
  JUL = 6,
  AUG = 7,
  SEP = 8,
  OCT = 9,
  NOV = 10,
  DEC = 11,
}

export type PlayerPosition = "TOP" | "JUNGLE" | "MID" | "ADC" | "SUPPORT";

interface ChampionMastery {
  championId: number;
  championLevel: number;
}

export interface ITeams {
  name: string;
  id: string;
  desciption_en: string;
  description_fin: string;
  website: string;
  twitter: string;
  discord: string;
  players: IPlayer[];
  image: string;
  wins: string;
  losses: string;
}

export const OApiReturns = {
  NextGameUnavailable: -5,
} as const;

export type ApiResponse<T> = {
  code: number;
  message?: string;
  body?: T;
};

export interface ILogin {
  username: string;
  password: string;
}

export interface PostApiResponse {
  status: number;
  message: string;
}

export const AuthRouteTables = {
  STAFF: "staff",
  MATCHES: "matches",
  TEAMS: "teams",
  PLAYERS: "players",
  PENDING: "pending",
  TRAFFIC: "traffic",
  FANTASY: "fantasy",
  ADMIN: "admin",
  AUTH: "auth",
} as const;

export interface IAnalyticsDailyRow {
  date: string;
  views: number;
  uniqueVisitors: number;
}

export interface IAnalyticsSummaryRow {
  path: string;
  count: number;
  uniqueVisitors: number;
}

export interface IAnalyticsTransitionRow {
  fromPath: string;
  toPath: string;
  count: number;
}

export interface IAnalyticsSummary {
  totalViews: number;
  totalUniqueVisitors: number;
  daily: IAnalyticsDailyRow[];
  pages: IAnalyticsSummaryRow[];
  transitions: IAnalyticsTransitionRow[];
}

export interface ITeamInfo {
  id: string;
  players?: IPlayer[];
  description_en: string | null;
  description_fin: string | null;
  discord: string | null;
  name: string;
  twitter: string | null;
  website: string | null;
}

export interface ITeamSeasonPlacement {
  season: string;
  place: number;
  wins: number;
  losses: number;
  gameWins: number;
  gameLosses: number;
  isChampion: boolean;
  teamCount: number;
}

export interface ITeamMatchHistoryGame {
  gameNumber: number;
  riotGameId?: number;
  result: string;
  champions: string[];
  opponentChampions: string[];
}

export interface ITeamMatchHistoryEntry {
  matchId: number;
  time: string;
  season: string;
  matchType: string;
  opponentTeamId: string;
  opponentTeamName: string;
  teamScore: number;
  opponentScore: number;
  result: string;
  games: ITeamMatchHistoryGame[];
}

export interface ITeamOverview {
  team: ITeamInfo;
  placements: ITeamSeasonPlacement[];
  matches: IMatch[];
  matchHistory?: ITeamMatchHistoryEntry[];
}

export interface IPlayerSeasonStats {
  season: string;
  teamId: string;
  playerName: string;
  role?: string;
  benched?: boolean;
  playerType?: string;
  kda?: number;
  kills?: number;
  deaths?: number;
  assists?: number;
  csPerMin?: number;
  kpPct?: number;
  dpm?: number;
  champsPlayed?: number;
  gamesPlayed?: number;
  doubleKills?: number;
  tripleKills?: number;
  quadraKills?: number;
  pentaKills?: number;
}

export interface IChampionRoleCounts {
  top: number;
  jungle: number;
  mid: number;
  adc: number;
  support: number;
}

export interface IChampionSeasonStats {
  season: string;
  champion: string;
  picks: number;
  bans: number;
  wins: number;
  losses: number;
  gamesTotal: number;
  winRate?: number;
  pickPct?: number;
  banPct?: number;
  presence?: number;
  gamesCounted?: number;
  kda?: number;
  kills?: number;
  deaths?: number;
  assists?: number;
  csPerMin?: number;
  goldPerMin?: number;
  dpm?: number;
  roles?: IChampionRoleCounts;
  source: string;
}

export interface ISeasonStatsMeta {
  seasons: string[];
  currentSeason: string;
}

export interface ITeamSeasonStats {
  season: string;
  teamId: string;
  statType: string;
  kills?: number;
  deaths?: number;
  assists?: number;
  csPerMin?: number;
  kpPct?: number;
  dpm?: number;
}

export interface IPlayerGameStats {
  id: number;
  season: string;
  teamId: string;
  playerName: string;
  gameNumber: number;
  opponentTeam?: string;
  gameLabel?: string;
  result: string;
  champion?: string;
  role?: string;
  kda?: number;
  kills?: number;
  deaths?: number;
  assists?: number;
  csPerMin?: number;
  dpm?: number;
  kpPct?: number;
  damage?: number;
  doubleKills?: number;
  tripleKills?: number;
  quadraKills?: number;
  pentaKills?: number;
  riotGameId?: number;
  matchId?: number;
  goldEarned?: number;
  level?: number;
  visionScore?: number;
  wardsPlaced?: number;
  wardsKilled?: number;
  minionsKilled?: number;
  goldPerMin?: number;
  visionPerMin?: number;
  goldPct?: number;
  dmgPct?: number;
  source?: string;
  riotPuuid?: string;
}

export interface IPendingGameParticipant {
  id: number;
  season: string;
  matchId: number;
  riotGameId: number;
  riotPuuid: string;
  riotSummonerName?: string;
  riotTeamId: number;
  inferredTeamId?: string;
  opponentTeam?: string;
  status: string;
  gameLabel?: string;
  result: string;
  champion?: string;
  kills?: number;
  deaths?: number;
  assists?: number;
}
